// „Занули склада" на листа на НОВА инвентаризация — въпросът и прилагането
// (сметката е в `stocktakeZeroing.ts`). Бутонът е в лентата на търсачката
// (`StocktakeSearchBar`, пропът `zeroAll`); в редакция го няма, защото
// наличностите към датата там вече включват самия документ.
//
// Въпросът е общият потвърждаващ диалог (тон warning) с ДВА избора-карти:
// „Занули всички (N)" и „Само минусите (M)". Отговорът само попълва
// „Намерено" 0 на избраните редове (`replaceAllRows` → редовете са „Преброени")
// и вдига черновата (`touch`); записът е обичайният Ctrl+Enter, затова
// отделни редове се поправят преди него. Празна „Бележка / причина" става
// „Зануляване на склада" — документът се намира с филтъра „Причина".
// Нищо за зануляване → съобщение, без въпрос.
import { useState } from "react";
import { App } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { StockBalance } from "../../../../api/inventory";
import { useStockBalances } from "../../../../hooks/useInventory";
import { formatMoney } from "../../components/numbers";
import { ConfirmDialog } from "../../components/confirm/ConfirmDialog";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import {
  buildZeroingPlan,
  zeroedRows,
  type ZeroingCandidate,
  type ZeroingPlan,
} from "./stocktakeZeroing";
import type { StocktakeComputedRow, StocktakeRowDraft } from "./stocktakeWorksheetShared";

type Params = {
  enabled: boolean; // само нова инвентаризация
  warehouseId: number | null;
  warehouseName: string | null;
  date: string; // YYYY-MM-DD
  rowsActions: {
    allRows: StocktakeComputedRow[];
    rows: Record<number, StocktakeRowDraft>;
    replaceAllRows: (rows: Record<number, StocktakeRowDraft>) => void;
  };
  balanceByProduct: Map<number, StockBalance>; // наличностите към датата (знакови)
  reason: string;
  setReason: (reason: string) => void;
  touch: () => void;
};

export const useStocktakeZeroing = ({
  enabled,
  warehouseId,
  warehouseName,
  date,
  rowsActions,
  balanceByProduct,
  reason,
  setReason,
  touch,
}: Params) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  // Докато наличностите към датата идват, празният списък не значи „празен
  // склад" — бутонът чака (същата заявка като на листа, от кеша)
  const { isLoading: stockLoading } = useStockBalances(warehouseId, date || null);
  // Планът остава до края на анимацията на затварянето (иначе текстът изчезва)
  const [plan, setPlan] = useState<ZeroingPlan | null>(null);
  const [open, setOpen] = useState(false);
  const dateLabel = date ? dayjs(date).format("DD.MM.YYYY") : "";
  const warehouse = warehouseName ?? "";

  const start = () => {
    const next = buildZeroingPlan(rowsActions.allRows, balanceByProduct);
    if (next.all.length === 0) {
      message.info(t("inventoryPage.stqZeroNothing", { warehouse, date: dateLabel }));
      return;
    }
    setPlan(next);
    setOpen(true);
  };

  const apply = (candidates: ZeroingCandidate[]) => {
    setOpen(false);
    rowsActions.replaceAllRows(zeroedRows(rowsActions.rows, candidates));
    if (!reason.trim()) setReason(t("inventoryPage.stqZeroReason"));
    touch();
    message.success(t("inventoryPage.stqZeroApplied", { count: candidates.length }));
  };

  const explanation = plan
    ? [
        t("inventoryPage.stqZeroText", { count: plan.all.length }),
        plan.negative.length > 0 && t("inventoryPage.stqZeroTextNegative", { count: plan.negative.length }),
        plan.alreadyCounted > 0 && t("inventoryPage.stqZeroTextCounted", { count: plan.alreadyCounted }),
        t("inventoryPage.stqZeroTextWhole"),
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const dialog = plan && (
    <ConfirmDialog
      open={open}
      intent="warning"
      icon={<ClearOutlined />}
      title={t("inventoryPage.stqZeroTitle", { warehouse, date: dateLabel })}
      explanation={explanation}
      preview={{
        caption: t("inventoryPage.stqZeroCaption", { date: dateLabel }),
        items: plan.all.map((candidate) => ({
          key: candidate.productId,
          label: candidate.name,
          value: formatQtyUnit(candidate.quantity, candidate.decimals, candidate.unit),
          hint: candidate.quantity < 0 ? t("inventoryPage.stqZeroNegativeTag") : undefined,
          tone: candidate.quantity < 0 ? "danger" : "default",
        })),
      }}
      choices={[
        {
          key: "all",
          label: t("inventoryPage.stqZeroAll", { count: plan.all.length }),
          hint:
            plan.estimatedShortage != null && plan.estimatedShortage > 0
              ? t("inventoryPage.stqZeroAllHint", { value: formatMoney(plan.estimatedShortage) })
              : t("inventoryPage.stqZeroAllHintNoPrice"),
          primary: true,
          onSelect: () => apply(plan.all),
        },
        {
          key: "negative",
          label: t("inventoryPage.stqZeroNegative", { count: plan.negative.length }),
          hint:
            plan.negative.length > 0
              ? t("inventoryPage.stqZeroNegativeHint")
              : t("inventoryPage.stqZeroNegativeNone"),
          disabled: plan.negative.length === 0,
          onSelect: () => apply(plan.negative),
        },
      ]}
      note={[
        { kind: "info", text: t("inventoryPage.stqZeroNoteDraft") },
        { kind: "undo", text: t("inventoryPage.stqZeroNoteUndo") },
      ]}
      onCancel={() => setOpen(false)}
      afterClose={() => setPlan(null)}
    />
  );

  return {
    // пропът на бутона в лентата на търсачката; undefined — бутон няма (редакция)
    button: enabled ? { disabled: warehouseId == null || stockLoading, onClick: start } : undefined,
    dialog,
  };
};
